// Shared extractor for the real message behind a supabase-js
// functions.invoke() failure.
//
// When an edge function returns a non-2xx status, supabase-js rejects with a
// FunctionsHttpError whose `.message` is the useless generic string
// "Edge Function returned a non-2xx status code" and leaves `data` null — the
// actual { ok:false, error } envelope sits UNREAD on `error.context`, a raw
// Response. Callers that only surfaced `error.message` therefore showed the
// generic string instead of the real reason (e.g. Whop's
// "You entered an invalid email address. Please enter a real email address.").
//
// This reads that body and returns the real message, preferring `error` then
// `message`, falling back to the raw thrown message, then a caller-supplied
// generic. Guards against the context being absent or a non-JSON body. Mirrors
// the per-call readers already in portalActions / aiTools / billingMessages,
// consolidated so the money/setup surfaces share one implementation.

const GENERIC_INVOKE_MESSAGE = 'Something went wrong. Please try again.'

/**
 * Best-effort real message from a caught functions.invoke() error.
 * @param error the value caught from `supabase.functions.invoke`
 * @param fallback used only when nothing else is available
 */
export async function messageFromInvokeError(
  error: unknown,
  fallback: string = GENERIC_INVOKE_MESSAGE,
): Promise<string> {
  // 1. The structured envelope on FunctionsHttpError.context (a Response).
  const ctx = (error as { context?: Response } | null)?.context
  if (ctx && typeof ctx.clone === 'function') {
    try {
      const body = (await ctx.clone().json()) as
        | { error?: unknown; message?: unknown }
        | null
        | undefined
      if (body) {
        if (typeof body.error === 'string' && body.error.trim()) return body.error.trim()
        if (typeof body.message === 'string' && body.message.trim()) return body.message.trim()
      }
    } catch {
      /* context unavailable, already-consumed, or a non-JSON body — fall through */
    }
  }
  // 2. The raw thrown message — but skip supabase-js's own generic string,
  //    which is exactly what we are trying to replace.
  const raw = (error as { message?: unknown } | null)?.message
  if (typeof raw === 'string' && raw.trim() && !/returned a non-2xx status code/i.test(raw)) {
    return raw.trim()
  }
  // 3. Nothing useful — the caller's generic fallback.
  return fallback
}
